import React, { useCallback, useEffect, useState, forwardRef, useRef, useMemo } from "react";

import {
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Persona,
  Textarea,
  Menu,
  MenuList,
  MenuItemRadio,
  MenuPopover,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Spinner,
  Subtitle1,
  makeStyles,
  mergeClasses,
  Divider,
  Link,
  useRestoreFocusTarget,
  MenuTrigger
} from "@fluentui/react-components";

import {
  SendFilled,
  AttachRegular,
  Emoji24Regular,
  CubeMultipleRegular,
  TranslateAuto16Filled,
  Translate16Filled,
  Translate16Regular,
  ChevronDownFilled
} from "@fluentui/react-icons";
import { MAX_MESSAGE_LENGTH, formatMessageBody } from "../../utils/chat-message";
import { useMaintainScrollPosition } from "../misc/useMaintainScrollPosition";
import { SoundBarAnimation } from "../room/ChatSidebar";
import { EmojiPicker } from "../room/EmojiPicker";
import { uploadV2 } from "../../utils/media-utils";
import { proxiedUrlFor } from "../../utils/media-url-utils";
import { spawnChatMessage } from "../chat-message";
import { PermissionNotification } from "../room/PermissionNotifications";
import { useRoomPermissions } from "../room/useRoomPermissions";
import { useRole } from "../room/useRole";
import PropTypes from "prop-types";
import { CloseButton } from "../input/CloseButton";
import AIAgents from "../../singleton/AIAgents";
import { translateText } from "../../utils/openAI-utils";
import { FormattedMessage } from "react-intl";
import { resolveDisplayedChatText } from "./utils/chat-message-text-utils";
import { useParticipantLayoutActionAvailability } from "./participant-layout/participant-layout-action-availability";

const useStyles = makeStyles({
  headerDrawer: {
    display: " flex",
    gap: " 8px",
    alignSelf: " stretch",
    backgroundColor: "var(--primaryColorBackground)"
  },
  drawerBody: {
    paddingTop: "20px"
  },
  sectionHeader: {
    display: " flex",
    padding: " 8px 0px",
    flexDirection: " column",
    alignItems: " flex-start",
    gap: "var(--Font-size-100, 10px)",
    alignSelf: "stretch"
  },
  sectionHeaderText: {
    color: "var(--color-text-secondary)"
  },
  sectionContent: {
    width: "100%",
    display: " flex",
    padding: " 4px",
    alignItems: " center",
    height: "48px",
    borderRadius: "4px",
    justifyContent: "space-between",
    gap: " 8px",
    "&:hover": {
      backgroundColor: "var(--accent1-color-hover)"
    }
  },
  persona: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    // height: "36px",
    "& .fui-Avatar__image": {
      // width: "36px",
      // height: "36px",
      // minWidth: "36px"
    },
    "& .fui-Avatar": {
      // height: "36px",
      // minWidth: "36px",
      // width: "36px",
    },
    "& .fui-Persona": {
      display: "flex",
      justifyContent: "center",
      alignItems: "center"
      // height: "36px"
    },
    "& span": {
      fontSize: "var(--Font-size-500, 20px)",
      fontStyle: "normal",
      fontWeight: "600",
      lineHeight: "var(--Line-height-500, 28px)"
    }
  },
  icons: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    "& > *": {
      cursor: "pointer"
    }
  },
  divider: {
    padding: "5px 0px 4px 0px"
  },
  contentParticipants: {},
  footerParticipants: {
    display: "flex",
    padding: "16px var(--Font-size-600, 24px) var(--Font-size-600, 24px) var(--Font-size-600, 24px)",
    justifyContent: "flex-end",
    width: "100%",
    backgroundColor: "var(--primaryColorBackground)"
  },
  redIcon: {
    color: "var(--color-danger)"
  },
  messageListContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "15px"
  },
  messageBubble: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "5px",
    paddingLeft: "40px",
    "& .message-author-container": {
      justifyContent: "flex-end",
      alignItems: "center",
      gap: "10px",
      display: "inline-flex"
    },
    "& .message-bubble-content": {
      display: "flex",
      padding: "20px 15px",
      justifyContent: "center",
      alignItems: "center",
      gap: "10px",
      alignSelf: "stretch",
      borderRadius: "20px 0px 20px 20px",
      background: "var(--secondaryColorBackground)",
      maxWidth: "280px",
      marginLeft: "auto"
    },
    "& .message-bubble-text": {
      color: "var(--color-text-primary)",
      textAlign: "left"
    },
    "& .spawn-message-container": {
      display: "flex",
      alignItems: "flex-end"
    }
  },
  messageBubbleUser: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "5px",
    "& .message-author-container": {
      justifyContent: "flex-start",
      alignItems: "center",
      gap: "10px",
      display: "inline-flex",
      flexDirection: "row-reverse"
    },
    "& .message-bubble-content": {
      display: "flex",
      padding: "20px 15px",
      justifyContent: "center",
      alignItems: "center",
      gap: "10px",
      alignSelf: "stretch",
      borderRadius: "0px 20px 20px 20px",
      background: "var(--secondaryColorBackground)",
      maxWidth: "280px",
      marginRight: "auto"
    },
    "& .spawn-message-container": {
      flexDirection: "row-reverse",
      display: "flex",
      alignItems: "flex-end"
    },
    "& .message-bubble-text": {
      color: "var(--color-text-primary)",
      textAlign: "left"
    }
  },
  messageAuthorDate: {
    textAlign: "right",
    color: "var(--color-text-secondary)",
    fontSize: 14,
    fontFamily: "Segoe UI",
    fontWeight: "400"
  },
  messageAuthor: { textAlign: "right", color: "white", fontSize: 14, fontFamily: "Segoe UI", fontWeight: "700" },
  messageBubbleText: {
    color: "var(--color-text-primary)",
    textAlign: "right"
  },
  sendStyles: {
    backgroundColor: "var(--color-success)",
    "&:hover": {
      backgroundColor: "var(--color-success)"
    },
    minWidth: "32px",
    width: "32px",
    minHeight: "32px",
    height: "32px",
    margin: "auto 0"
  },
  sendStylesDisabled: {
    backgroundColor: "var(--accent1-color-hover)",
    minWidth: "32px",
    width: "32px",
    minHeight: "32px",
    height: "32px",
    margin: "auto 0"
  },
  textareaInput: {
    "& > :first-child": {
      resize: "none",
      minHeight: "32px"
    }
  },
  sendMessageContainer: {
    display: "flex",
    gap: "8px",
    width: "100%",
    justifyContent: "space-between"
  },
  inputIcon: {
    minWidth: "20px",
    width: "20px"
  },
  chatSoundbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: "40px",
    minWidth: "40px",
    height: "40px",
    marginLeft: "12px",
    marginRight: "12px"
  },
  chatMessageList: {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
    paddingBottom: "60px"
  },
  chatMessageBubble: {
    wordBreak: "break-word",
    "& p": {
      margin: "0 0 0.5em 0",
      "&:last-child": {
        marginBottom: 0
      }
    },
    "& strong": {
      fontWeight: "bold",
      fontSize: "inherit"
    },
    "& em": {
      fontStyle: "italic"
    },
    "& h1, & h2, & h3, & h4, & h5, & h6": {
      marginBottom: "0.5em",
      lineHeight: "normal"
    },
    "& ul, & ol": {
      margin: "0.5em 0 0.5em 1.5em",
      paddingLeft: "1.5em"
    },
    "& ul": {
      listStyleType: "disc"
    },
    "& ol": {
      listStyleType: "decimal"
    },
    "& li": {
      marginBottom: "0.25em",
      lineHeight: 1.4
    },
    "& code": {
      fontFamily: "monospace",
      backgroundColor: "var(--accent1-color-hover)",
      padding: "0.1em 0.3em",
      borderRadius: "3px",
      fontSize: "0.9em",
      wordBreak: "break-all"
    },
    "& pre": {
      fontFamily: "monospace",
      backgroundColor: "var(--color-overlay)",
      padding: "1em",
      borderRadius: "4px",
      overflowX: "auto",
      margin: "0.5em 0",
      fontSize: "0.9em",
      wordBreak: "break-all",
      whiteSpace: "pre-wrap"
    },
    "& a": {
      textDecoration: "underline"
    },
    "& .chat-emoji": {
      verticalAlign: "middle"
    }
  },
  dotFlashing: {
    position: "relative",
    width: "8px",
    height: "8px",
    borderRadius: "8px",
    backgroundColor: "var(--action-text-color)",
    color: "var(--action-text-color)",
    animation: "dotFlashing 1s infinite linear alternate",
    animationDelay: "0.5s",
    marginLeft: "10px",
    marginRight: "10px",
    "&::before": {
      content: '""',
      display: "inline-block",
      position: "absolute",
      top: "0",
      left: "-12.5px",
      width: "8px",
      height: "8px",
      borderRadius: "8px",
      backgroundColor: "var(--action-text-color)",
      color: "var(--action-text-color)",
      animation: "dotFlashing 1s infinite alternate",
      animationDelay: "0s"
    },
    "&::after": {
      content: '""',
      display: "inline-block",
      position: "absolute",
      top: "0",
      left: "12.5px",
      width: "8px",
      height: "8px",
      borderRadius: "8px",
      backgroundColor: "var(--action-text-color)",
      color: "var(--action-text-color)",
      animation: "dotFlashing 1s infinite alternate",
      animationDelay: "1s"
    }
  },
  translationBar: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "10px",
    padding: "10px 16px 0px 16px",
    width: "100%"
  },
  languageButton: {
    marginLeft: "4px",
    color: "var(--primary-color)",
    // All combinations of hover, active, focus, etc.
    "&:hover, &:active, &:focus, &:hover:active, &:hover:focus, &:active:focus, &:hover:active:focus": {
      color: "var(--primary-color)!important;"
    }
  },
  languageList: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: "8px",
    minWidth: "140px"
  },
  languageItem: {
    padding: "8px 12px",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px"
  },
  languageItemSelected: {
    backgroundColor: "var(--color-brand-subtle)",
    color: "var(--primary-color)",
    fontWeight: "600"
  },
  translatedLabel: {
    marginLeft: "4px",
    fontSize: "11px",
    color: "var(--colorNeutralForeground3)",
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline"
    }
  },
  onDemandTranslateContainer: {
    display: "flex",
    alignItems: "center"
  },
  systemMessageContainer: {
    display: "flex",
    justifyContent: "center",
    width: "100%"
  },
  systemMessagePill: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    backgroundColor: "var(--secondaryColorBackground)",
    color: "var(--color-text-primary)",
    padding: "10px 16px",
    borderRadius: "5px",
    fontSize: "12px",
    textTransform: "uppercase",
    fontWeight: "semibold",
    width: "100%",
    justifyContent: "center"
  },
  systemMessagePillDisabled: {
    backgroundColor: "var(--color-text-secondary)"
  },
  systemMessagePillError: {
    backgroundColor: "var(--color-danger)"
  }
});

function useCooldown(delay = 500) {
  const [cooldown, setCooldown] = useState(false);
  const timeoutRef = useRef();

  const trigger = useCallback(
    fn => {
      if (!cooldown) {
        fn();
        setCooldown(true);
        timeoutRef.current = setTimeout(() => setCooldown(false), delay);
      }
    },
    [cooldown, delay]
  );

  useEffect(() => () => clearTimeout(timeoutRef.current), []);
  return trigger;
}
// GLOBAL state (shared across all chats)
let sharedTargetLanguage = ""; // persists across mount/unmount, shared across chats
let sharedShowOriginals = false; // global "See originals" toggle
const sharedTranslatedMessages = new Map(); // messageId -> { text, language }
const sharedSkipMessages = new Set(); // messageIds that existed when language was enabled/changed
const sharedInFlightTranslations = new Set(); // messageIds currently being translated (prevents duplicate requests)
const sharedOnDemandTranslations = new Map(); // messageId -> { language, text } — persists on-demand translations across mount/unmount

// PER-CHAT state (pills and pill-tracking only — so changing language in private chat
// doesn't overwrite the public chat's system message pill)
const chatPillStates = new Map(); // chatKey -> { languageEvents, prevTargetLanguage }

function getChatPillState(chatKey) {
  if (!chatPillStates.has(chatKey)) {
    chatPillStates.set(chatKey, {
      languageEvents: [],
      prevTargetLanguage: "" // tracks when this chat last saw a language change
    });
  }
  return chatPillStates.get(chatKey);
}

export default function FluentChatOverlay({
  hubChannel,
  toggleChat,
  selectedUser,
  messageGroups,
  onSendMessage,
  onUploadAttachments,
  onKeyDown,
  message,
  setMessage,
  isCommand,
  isDisabled,
  onSpawn,
  inputRef,
  onSelectEmoji,
  canTextChat
}) {
  const styles = useStyles();

  // Derive a unique key for this chat context (public vs private per user)
  const chatKey = selectedUser ? `private-${selectedUser.id}` : "public";

  const [globalMenuOpen, setGlobalMenuOpen] = useState(false);
  const globalTriggerRef = useRef(null);
  const globalPositioningRef = useRef(null);

  const onGlobalMenuOpenChange = (e, data) => {
    if (data.type === "clickOutside" && e.target === globalTriggerRef.current) {
      return;
    }
    setGlobalMenuOpen(data.open);
  };

  useEffect(() => {
    if (globalTriggerRef.current) {
      globalPositioningRef.current?.setTarget(globalTriggerRef.current);
    }
  }, [globalTriggerRef, globalPositioningRef]);

  const [onScrollList, listRef, scrolledToBottom] = useMaintainScrollPosition(messageGroups);

  const { canSpawnChatMessages } = useParticipantLayoutActionAvailability();
  const canSpawnMessages = hubChannel.can("spawn_and_move_media") && canSpawnChatMessages;
  const isMobile = AFRAME.utils.device.isMobile();

  const onSpawnMessage = useCallback(() => {
    spawnChatMessage(message);
    setMessage("");
  }, [message, setMessage]);

  const { text_chat: textChatEnabled } = useRoomPermissions();

  const isMod = useRole("owner");

  // ── Translation feature ──
  // Language selection is GLOBAL (shared across all chats).
  // Pills are SCOPED per chat. Everything else is shared.
  const [translatorAgent, setTranslatorAgent] = useState(null);
  const [targetLanguage, _setTargetLanguage] = useState(sharedTargetLanguage);
  const setTargetLanguage = useCallback(lang => {
    // Snapshot all current message IDs as "skip" — they existed before this language change
    if (lang) {
      const mg = messageGroupsRef.current;
      if (mg) {
        mg.forEach(group => {
          if (group.messages?.length > 0) {
            group.messages.forEach(msg => sharedSkipMessages.add(msg.id));
          }
        });
      }
    }
    sharedTargetLanguage = lang;
    _setTargetLanguage(lang);
  }, []);
  const translationEnabled = !!targetLanguage;
  const [translationVersion, setTranslationVersion] = useState(0); // trigger re-render
  const [showOriginals, _setShowOriginals] = useState(sharedShowOriginals);
  const setShowOriginals = useCallback(val => {
    const v = typeof val === "function" ? val(sharedShowOriginals) : val;
    sharedShowOriginals = v;
    _setShowOriginals(v);
  }, []);

  // Sync React state from shared globals when switching between chat contexts
  useEffect(() => {
    _setTargetLanguage(sharedTargetLanguage);
    _setShowOriginals(sharedShowOriginals);
  }, [chatKey]);

  const messageGroupsRef = useRef(messageGroups);
  useEffect(() => {
    messageGroupsRef.current = messageGroups;
  }, [messageGroups]);

  useEffect(() => {
    const ps = getChatPillState(chatKey);
    if (targetLanguage === ps.prevTargetLanguage) return;

    const now = Date.now();
    const mg = messageGroupsRef.current;

    // If the last event is a translationError pill, don't overwrite it with a
    // "disabled" pill — the error pill already communicates the failure and
    // setTargetLanguage(null) was called programmatically by the error handler.
    if (!targetLanguage && ps.languageEvents.length > 0) {
      const lastEv = ps.languageEvents[ps.languageEvents.length - 1];
      if (lastEv.systemType === "translationError") {
        ps.prevTargetLanguage = targetLanguage;
        return;
      }
    }

    // Snapshot all current messages as "skip" — they existed before this language change
    if (targetLanguage && mg) {
      mg.forEach(group => {
        if (group.messages?.length > 0) {
          group.messages.forEach(msg => sharedSkipMessages.add(msg.id));
        }
      });
    }

    // Find the timestamp of the latest individual message (not group creation time)
    // because messages appended to existing groups don't update the group timestamp.
    let latestMsgTimestamp = 0;
    if (mg) {
      mg.forEach(group => {
        if (group.messages?.length > 0) {
          const lastMsg = group.messages[group.messages.length - 1];
          if (lastMsg.timestamp && lastMsg.timestamp > latestMsgTimestamp) {
            latestMsgTimestamp = lastMsg.timestamp;
          }
        } else if (group.timestamp && group.timestamp > latestMsgTimestamp) {
          latestMsgTimestamp = group.timestamp;
        }
      });
    }

    const newEvent = {
      id: `sys-trans-${chatKey}-${now}`,
      systemMessage: true,
      systemType: "translation",
      language: targetLanguage || null,
      disabled: !targetLanguage,
      timestamp: now
    };

    // If the last event exists and no new messages arrived since it was created,
    // replace it in-place (keep its original timestamp so it stays at same position).
    if (ps.languageEvents.length > 0) {
      const lastEvent = ps.languageEvents[ps.languageEvents.length - 1];
      if (latestMsgTimestamp <= lastEvent.timestamp) {
        // No new messages since last pill — update in place
        newEvent.timestamp = lastEvent.timestamp;
        newEvent.id = lastEvent.id;
        ps.languageEvents = [...ps.languageEvents.slice(0, -1), newEvent];
      } else {
        // New messages arrived since last pill — append new pill
        ps.languageEvents = [...ps.languageEvents, newEvent];
      }
    } else {
      ps.languageEvents = [newEvent];
    }

    ps.prevTargetLanguage = targetLanguage;
    // Force re-render immediately so the pill appears without waiting for translations
    setTranslationVersion(v => v + 1);
  }, [targetLanguage, chatKey]);

  // Detect translator agent on mount and when agents register
  useEffect(() => {
    const checkForTranslator = () => {
      try {
        const agent = AIAgents.getTranslatorAgent();
        if (agent) {
          setTranslatorAgent(agent);
        }
      } catch (e) {
        // AIAgents not ready yet
      }
    };

    const handleAgentRegistered = event => {
      if (event.detail?.provider === "OpenAI Translator") {
        checkForTranslator();
      }
    };

    checkForTranslator();
    window.addEventListener("ai-agent-registered", handleAgentRegistered);

    return () => {
      window.removeEventListener("ai-agent-registered", handleAgentRegistered);
    };
  }, []);

  // Translate only NEW incoming messages that arrived AFTER the language was enabled/changed.
  // sharedSkipMessages contains all message IDs that existed at the moment the user picked a language,
  // so they are skipped. Already-translated messages are never re-translated on language change.
  useEffect(() => {
    if (!translatorAgent || !targetLanguage || !messageGroups) return;

    const allMessages = [];

    messageGroups.forEach(group => {
      if (!group.sent && group.messages?.length > 0) {
        group.messages.forEach(msg => {
          const bodyText = typeof msg.body === "string" ? msg.body : msg.body?.message;
          const isValidMessage =
            bodyText && typeof bodyText === "string" && (msg.type === "chat" || msg.type === "private-chat");

          if (!isValidMessage) return;

          if (sharedSkipMessages.has(msg.id)) return;
          if (sharedTranslatedMessages.has(msg.id)) return;
          // Prevent duplicate requests while a translation is already in-flight
          if (sharedInFlightTranslations.has(msg.id)) return;

          allMessages.push({ id: msg.id, text: bodyText });
        });
      }
    });

    if (allMessages.length === 0) return;

    // Mark all as in-flight before starting the async request
    allMessages.forEach(m => sharedInFlightTranslations.add(m.id));

    const doTranslate = async () => {
      try {
        const texts = allMessages.map(m => m.text);
        const result = await translateText({
          uuid: translatorAgent.objectUuid,
          targetLanguage,
          texts
        });

        if (result.error) {
          console.error("Translation error:", result.error);

          const ps = getChatPillState(chatKey);
          const now = Date.now();

          let localLatestMsgTimestamp = 0;
          const mg = messageGroupsRef.current;
          if (mg) {
            for (const group of mg) {
              for (const msg of group.messages || []) {
                if (msg.timestamp > localLatestMsgTimestamp) localLatestMsgTimestamp = msg.timestamp;
              }
            }
          }

          const errorEvent = {
            id: `error-${now}`,
            systemMessage: true,
            systemType: "translationError",
            errorMessage: result.error.message,
            timestamp: now
          };

          if (ps.languageEvents.length > 0) {
            const lastEvent = ps.languageEvents[ps.languageEvents.length - 1];
            if (lastEvent.systemType === "translationError" && localLatestMsgTimestamp <= lastEvent.timestamp) {
              errorEvent.timestamp = lastEvent.timestamp;
              errorEvent.id = lastEvent.id;
              ps.languageEvents = [...ps.languageEvents.slice(0, -1), errorEvent];
            } else {
              ps.languageEvents = [...ps.languageEvents, errorEvent];
            }
          } else {
            ps.languageEvents = [errorEvent];
          }

          if (result.error.severity === "blocking") {
            setTargetLanguage(null);
          }

          setTranslationVersion(v => v + 1);
          return;
        }

        if (result.translations && result.translations.length === allMessages.length) {
          allMessages.forEach((m, i) => {
            sharedTranslatedMessages.set(m.id, {
              text: result.translations[i].translatedText,
              language: targetLanguage
            });
          });
          setTranslationVersion(v => v + 1);
        }
      } finally {
        // Clear in-flight status regardless of success/failure
        allMessages.forEach(m => sharedInFlightTranslations.delete(m.id));
      }
    };

    doTranslate();
  }, [messageGroups, translatorAgent, targetLanguage, chatKey]);

  const inputContentAfter = useMemo(() => {
    return (
      <div style={{ display: "flex", gap: "4px" }}>
        {!isMobile && <EmojiPickerPopoverButton onSelectEmoji={onSelectEmoji} />}
        {message.length === 0 && canSpawnMessages ? <MessageAttachmentButton onChange={onUploadAttachments} /> : null}
        {canSpawnMessages && (
          <Button
            className={styles.inputIcon}
            appearance="transparent"
            disabled={isDisabled}
            onClick={onSpawnMessage}
            icon={<CubeMultipleRegular />}
          />
        )}
      </div>
    );
  }, [
    isMobile,
    onSelectEmoji,
    message,
    canSpawnMessages,
    onUploadAttachments,
    isDisabled,
    onSpawnMessage,
    styles.inputIcon
  ]);

  // Merge message groups and system events chronologically.
  // For message groups, we use the LAST message's timestamp (not the group creation time)
  // because new messages can be appended to an existing group and the pill must appear
  // between groups based on when the language was actually changed.
  const messageGroupsWithSystem = useMemo(() => {
    const events = [...getChatPillState(chatKey).languageEvents];
    const groups = messageGroups || [];

    if (groups.length === 0) return events;
    if (events.length === 0) return groups;

    // Helper: get the effective end-timestamp of a message group
    const groupEndTimestamp = group => {
      if (group.messages?.length > 0) {
        return group.messages[group.messages.length - 1].timestamp || group.timestamp;
      }
      return group.timestamp;
    };

    // Insert events between groups based on timestamps.
    // An event with timestamp T should appear after all groups whose last message is <= T
    // and before any group whose first new message is > T.
    const merged = [];
    let ei = 0;

    for (let gi = 0; gi < groups.length; gi++) {
      const group = groups[gi];
      const groupStart = group.timestamp;

      // Insert any events that belong before this group starts
      while (ei < events.length && events[ei].timestamp < groupStart) {
        merged.push(events[ei++]);
      }

      // Check if any events fall within this group (between existing messages).
      // If so, we need to split the group.
      const midEvents = [];
      while (ei < events.length && events[ei].timestamp <= groupEndTimestamp(group)) {
        midEvents.push(events[ei++]);
      }

      if (midEvents.length === 0) {
        // No events in the middle of this group — push it whole
        merged.push(group);
      } else {
        // Split the group around the events.
        // Find which messages come before vs after each event.
        const msgs = group.messages || [];
        let mi = 0;

        for (const evt of midEvents) {
          // Collect messages that came before this event
          const beforeMsgs = [];
          while (mi < msgs.length && (msgs[mi].timestamp || 0) <= evt.timestamp) {
            beforeMsgs.push(msgs[mi++]);
          }
          if (beforeMsgs.length > 0) {
            merged.push({
              ...group,
              id: `${group.id}-before-${evt.id}`,
              messages: beforeMsgs
            });
          }
          merged.push(evt);
        }

        // Remaining messages after all events
        if (mi < msgs.length) {
          merged.push({
            ...group,
            id: `${group.id}-after`,
            messages: msgs.slice(mi)
          });
        }
      }
    }

    // Any remaining events after all groups
    while (ei < events.length) merged.push(events[ei++]);

    return merged;
  }, [messageGroups, translationVersion]);

  return (
    <>
      <DrawerHeader className={styles.headerDrawer}>
        <DrawerHeaderTitle style={{ width: "100%" }} action={<CloseButton onClick={toggleChat} />}>
          {selectedUser && (
            <Persona
              size="large"
              className={styles.persona}
              name={`${selectedUser?.profile.displayName}`}
              avatar={{
                image: {
                  src: selectedUser?.profile?.thumbnail
                }
              }}
            />
          )}
          {!selectedUser && (
            <Subtitle1>
              <FormattedMessage id="chat.everyone" defaultMessage="Everyone" />
            </Subtitle1>
          )}
        </DrawerHeaderTitle>
      </DrawerHeader>

      {/* Translation controls */}
      {translatorAgent && (
        <div className={styles.translationBar}>
          <div>
            <span className={styles.translateLabel}>
              <TranslateAuto16Filled /> <FormattedMessage id="chat.translateTo" defaultMessage="Translate to" />
            </span>
            <Menu
              appearance="transparent"
              checkedValues={{ language: [targetLanguage] }}
              onCheckedValueChange={(_, { checkedItems }) => setTargetLanguage(checkedItems[0])}
              open={globalMenuOpen}
              onOpenChange={onGlobalMenuOpenChange}
              positioning={{ positioningRef: globalPositioningRef }}
            >
              <MenuTrigger>
                <Link role="button" className={styles.languageButton}>
                  <>
                    {targetLanguage || <FormattedMessage id="chat.selectLanguage" defaultMessage="Select language" />}
                    <ChevronDownFilled />
                  </>
                </Link>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItemRadio key="none" name="language" value="">
                    <FormattedMessage id="chat.none" defaultMessage="None" />
                  </MenuItemRadio>
                  {(translatorAgent.targetLanguages || []).map(lang => (
                    <MenuItemRadio key={lang} name="language" value={lang}>
                      {lang}
                    </MenuItemRadio>
                  ))}
                </MenuList>
              </MenuPopover>
            </Menu>
            {sharedTranslatedMessages.size > 0 && (
              <span className={styles.translatedLabel} onClick={() => setShowOriginals(prev => !prev)}>
                {showOriginals ? "(See translations)" : "(See originals)"}
              </span>
            )}
          </div>
          <Divider />
        </div>
      )}

      <DrawerBody className={styles.drawerBody} ref={listRef} onScroll={onScrollList}>
        <ChatMessageList className={styles.chatMessageList}>
          {messageGroupsWithSystem.map(entry => {
            const { id, systemMessage, systemType, language, disabled, type } = entry;
            if (systemMessage) {
              if (systemType === "translationError") {
                return (
                  <div key={id} className={styles.systemMessageContainer}>
                    <div className={mergeClasses(styles.systemMessagePill, styles.systemMessagePillError)}>
                      <Translate16Regular /> {entry.errorMessage}
                    </div>
                  </div>
                );
              }
              if (systemType === "translation") {
                return (
                  <div key={id} className={styles.systemMessageContainer}>
                    <div className={mergeClasses(styles.systemMessagePill, disabled && styles.systemMessagePillDisabled)}>
                      {disabled ? (
                        <><Translate16Regular /> TRANSLATION IS DISABLED</>
                      ) : (
                        <><Translate16Regular /> {language} TRANSLATION IS ENABLED</>
                      )}
                    </div>
                  </div>
                );
              }
              // return <SystemMessage key={id} {...entry} />;
            } else {
              if (type === "permission") {
                // return <PermissionMessageGroup key={id} {...entry} />;
              } else {
                return (
                  <ChatMessageGroup
                    {...entry}
                    onSpawn={onSpawn}
                    canSpawnMessages={canSpawnMessages}
                    key={`chat-message-group-${id}`}
                    translatedMessages={sharedTranslatedMessages}
                    translationEnabled={translationEnabled}
                    targetLanguage={targetLanguage}
                    translationVersion={translationVersion}
                    translatorAgent={translatorAgent}
                    showOriginals={showOriginals}
                  />
                );
              }
            }
          })}
        </ChatMessageList>
        {/* {!canTextChat && <PermissionNotification permission={"text_chat"} />} */}
        {!textChatEnabled && isMod && <PermissionNotification permission={"text_chat"} isMod={true} />}
      </DrawerBody>

      <div className={styles.footerParticipants}>
        {canTextChat && (
          <div className={styles.sendMessageContainer}>
            <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
              <Textarea
                ref={inputRef}
                onChange={e => setMessage(e.target.value)}
                value={message}
                onKeyDown={onKeyDown}
                maxLength={MAX_MESSAGE_LENGTH}
                appearance="filled-ligther"
                placeholder="Type message here"
                className={styles.textareaInput}
                style={{ display: "flex", flex: 1 }}
              />
              {inputContentAfter}
            </div>
            <Button
              className={isDisabled && !isCommand ? styles.sendStylesDisabled : styles.sendStyles}
              onClick={onSendMessage}
              disabled={isDisabled && !isCommand}
              icon={<SendFilled />}
            />
          </div>
        )}
        {!canTextChat && <PermissionNotification permission={"text_chat"} />}
      </div>
    </>
  );
}

export const ChatMessageGroup = React.memo(function ChatMessageGroup({ messages, ...commonProps }) {
  return (
    <>
      {messages.map(message => (
        <MemoizedMessageComponent
          message={message}
          key={`chat-message-${message.id}`} // Use stable message ID
          canSpawnMessages={commonProps.canSpawnMessages}
          sender={commonProps.sender}
          timestamp={commonProps.timestamp}
          sent={commonProps.sent}
          onSpawn={commonProps.onSpawn}
          loading={commonProps.loading}
          playAudio={commonProps.playAudio}
          cancelAllPlay={commonProps.cancelAllPlay}
          translatedMessages={commonProps.translatedMessages}
          translationEnabled={commonProps.translationEnabled}
          targetLanguage={commonProps.targetLanguage}
          translationVersion={commonProps.translationVersion}
          translatorAgent={commonProps.translatorAgent}
          showOriginals={commonProps.showOriginals}
        />
      ))}
    </>
  );
});

function ChatMessage({
  canSpawnMessages,
  message,
  isMe,
  children,
  timestamp,
  sender,
  onSpawn,
  loading,
  forceSpawn,
  actionChildren = <></>,
  spawnMessageStyles = {},
  footer = null
}) {
  const styles = useStyles();

  const formatTime = timestamp => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
  };

  return (
    <div className={isMe ? styles.messageBubble : styles.messageBubbleUser}>
      <div className={"message-author-container"}>
        <span className={styles.messageAuthorDate}>{formatTime(timestamp)}</span>
        <span className={styles.messageAuthor}>{isMe ? "You" : sender}</span>
      </div>
      <div className="spawn-message-container" style={spawnMessageStyles}>
        {(message?.body?.canSpawn || forceSpawn) && canSpawnMessages && (
          <Button appearance="transparent" icon={<CubeMultipleRegular />} onClick={() => onSpawn(message?.body?.src)} />
        )}
        {actionChildren}
        <div className={"message-bubble-content"}>
          {!loading && <div className={mergeClasses("message-bubble-text", styles.chatMessageBubble)}>{children}</div>}
          {loading && (
            <div className="snippet" data-title="dot-flashing">
              <div className="stage">
                <div className={styles.dotFlashing}></div>
              </div>
            </div>
          )}
        </div>
      </div>
      {footer}
    </div>
  );
}

ChatMessageGroup.propTypes = {
  messages: PropTypes.array.isRequired,
  canSpawnMessages: PropTypes.bool.isRequired,
  sender: PropTypes.string.isRequired,
  timestamp: PropTypes.number.isRequired,
  sent: PropTypes.bool.isRequired,
  onSpawn: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  playAudio: PropTypes.func,
  cancelAllPlay: PropTypes.func
};

export const ChatMessageList = forwardRef(({ children, ...rest }, ref) => (
  <ul {...rest} ref={ref}>
    {children}
  </ul>
));

ChatMessageList.displayName = "ChatMessageList";

const isImage = url => {
  if (typeof url !== "string") return false;

  // Check if the URL has an image file extension (quick check)
  const imageExtensions = ["png", "jpg", "jpeg", "gif", "bmp", "webp"];
  const urlExtension = url.split(".").pop().toLowerCase();

  if (imageExtensions.includes(urlExtension)) {
    return true;
  }

  return false;
};

function MessageComponent({ message, ...additionalProps }) {
  const styles = useStyles();
  const {
    canSpawnMessages,
    sender,
    timestamp,
    sent: isMe,
    onSpawn,
    loading,
    key,
    translatedMessages,
    translationEnabled,
    translatorAgent,
    showOriginals
  } = additionalProps;

  const [menuOpen, setMenuOpen] = useState(false);
  const triggerRef = useRef(null);
  const positioningRef = useRef(null);

  const onMenuOpenChange = (e, data) => {
    if (data.type === "clickOutside" && e.target === triggerRef.current) {
      return;
    }
    setMenuOpen(data.open);
  };

  useEffect(() => {
    if (triggerRef.current) {
      positioningRef.current?.setTarget(triggerRef.current);
    }
  }, [triggerRef, positioningRef]);

  // Translation state for this message
  // Translations persist even after the overall selector is set to "None" —
  // previously translated messages keep showing their translation with a toggle.
  // The localViewMode controls if this message respects standard global showing,
  // or is manually overridden.
  const [localViewMode, setLocalViewMode] = useState(
    () => (sharedOnDemandTranslations.has(message.id) ? "translation" : "default")
  );

  // On-demand translation state (per-message) — seeded from shared map so it survives remounts
  const [onDemandTranslation, _setOnDemandTranslation] = useState(
    () => sharedOnDemandTranslations.get(message.id) || null
  );
  const setOnDemandTranslation = val => {
    if (val) {
      sharedOnDemandTranslations.set(message.id, val);
    }
    _setOnDemandTranslation(val);
  };
  const [onDemandLoading, setOnDemandLoading] = useState(false);
  const [onDemandError, setOnDemandError] = useState(null);

  useEffect(() => {
    setLocalViewMode("default");
  }, [showOriginals]);

  const translationData = translatedMessages?.get(message.id);
  const translatedText = translationData?.text;

  const isGlobalTranslationShowing =
    localViewMode === "translation" ? true : localViewMode === "original" ? false : !showOriginals;
  const shouldShowTranslation = !!(translatedText && !isMe && isGlobalTranslationShowing);

  const isOnDemandTranslationShowing = localViewMode === "original" ? false : true;
  const showOnDemandOverride = !!(onDemandTranslation && isOnDemandTranslationShowing);

  const isShowingTranslation = showOnDemandOverride || shouldShowTranslation;

  const handleToggleView = () => {
    if (isShowingTranslation) {
      setLocalViewMode("original");
    } else {
      setLocalViewMode("translation");
    }
  };

  const handleOnDemandTranslate = async language => {
    if (!translatorAgent || !message.body) return;

    const bodyText = typeof message.body === "string" ? message.body : message.body?.message || message.body;
    if (!bodyText || typeof bodyText !== "string") return;

    setOnDemandError(null);
    setLocalViewMode("translation");
    setOnDemandLoading(true);
    try {
      const result = await translateText({
        uuid: translatorAgent.objectUuid,
        targetLanguage: language,
        texts: [bodyText]
      });
      if (result.error) {
        setOnDemandError(result.error.message);
      } else if (result.translations && result.translations.length > 0) {
        setOnDemandTranslation({ language, text: result.translations[0].translatedText });
      }
    } catch (e) {
      console.error("On-demand translation failed:", e);
      setOnDemandError("Translation failed. Please try again.");
    } finally {
      setOnDemandLoading(false);
    }
  };

  // Add cooldown for SoundBarAnimation clicks
  const playAudioWithCooldown = useCooldown(500);
  const cancelAllPlayWithCooldown = useCooldown(500);

  const bodyContent = message?.body?.message !== undefined ? message.body : message;
  const bodyMessage = bodyContent.message || message.body;

  if (loading) {
    return <ChatMessage isMe={isMe} loading={loading} sender={sender} timestamp={timestamp} />;
  }

  //Handle Images From AI
  if (bodyMessage && typeof bodyMessage === "string" && bodyMessage?.startsWith("https") && isImage(bodyMessage)) {
    return (
      <ChatMessage canSpawnMessages={canSpawnMessages} sender={sender} timestamp={timestamp} isMe={isMe}>
        <div className={styles.imageAi}>
          <img src={proxiedUrlFor(message.body)}></img>
          {/* <a href="#" onClick={sendToScene}>Open in Metaverse</a> */}
        </div>
      </ChatMessage>
    );
  }

  switch (message.type) {
    case "private-chat": {
      const privateBodyText = typeof message.body === "string" ? message.body : message.body?.message || "";
      const { formattedBody } = formatMessageBody(privateBodyText);
      const { cancelAllPlay, playAudio } = additionalProps || {};

      let actionChildren = null;
      let spawnMessageStyles = undefined;

      if (message.loadingAudio && message.playable && sender !== "You") {
        // Render loading state with Spinner
        actionChildren = (
          <div className={styles.chatSoundbar}>
            <Spinner />
          </div>
        );
        spawnMessageStyles = { alignItems: "center" };
      } else if ((message.audio != null || message.playable) && !message.play) {
        actionChildren = (
          <SoundBarAnimation
            onClick={() => playAudioWithCooldown(() => playAudio(message))}
            containerClassName={styles.chatSoundbar}
            play={false}
          />
        );
        spawnMessageStyles = { alignItems: "center" };
      } else if (message.play) {
        actionChildren = (
          <SoundBarAnimation
            play={message.play}
            onClick={() => cancelAllPlayWithCooldown(cancelAllPlay)}
            containerClassName={styles.chatSoundbar}
          />
        );
        spawnMessageStyles = { alignItems: "center" };
      }

      return (
        <ChatMessage
          key={key}
          canSpawnMessages={canSpawnMessages}
          sender={sender}
          timestamp={timestamp}
          isMe={isMe}
          spawnMessageStyles={spawnMessageStyles}
          actionChildren={actionChildren}
        >
          {formattedBody}
        </ChatMessage>
      );
    }
    case "chat": {
      const showOnDemand = showOnDemandOverride;
      const showGlobal = !showOnDemand && shouldShowTranslation;
      const displayText = resolveDisplayedChatText({
        messageBody: message.body,
        translatedText,
        onDemandTranslation,
        shouldShowTranslation: showGlobal,
        showOnDemandOverride: showOnDemand
      });

      const { formattedBody } = formatMessageBody(displayText);

      // Determine the translation label — show toggle for any translated message
      const hasAnyTranslation = onDemandTranslation || translatedText;

      const footer = (
        <>
          {/* On-demand translation dropdown - below the bubble */}
          {translatorAgent && !isMe && (
            <div
              className={styles.onDemandTranslateContainer}
              style={{ justifyContent: isMe ? "flex-end" : "flex-start", paddingLeft: isMe ? "40px" : "0" }}
            >
              {onDemandLoading ? (
                <Spinner size="tiny" />
              ) : (
                <>
                  <span className={styles.translateLabel}>
                    <Translate16Filled /> <FormattedMessage id="chat.translateTo" defaultMessage="Translate to" />
                  </span>
                  <Menu
                    appearance="transparent"
                    checkedValues={{
                      language: [onDemandTranslation?.language || translationData?.language || ""]
                    }}
                    onCheckedValueChange={(_, { checkedItems }) => handleOnDemandTranslate(checkedItems[0])}
                    open={menuOpen}
                    onOpenChange={onMenuOpenChange}
                    positioning={{ positioningRef }}
                  >
                    <MenuTrigger>
                      <Link role="button" className={styles.languageButton} onClick={() => setMenuOpen(s => !s)}>
                        <>
                          {onDemandTranslation?.language || translationData?.language || (
                            <FormattedMessage id="chat.selectLanguage" defaultMessage="Select language" />
                          )}
                          <ChevronDownFilled />
                        </>
                      </Link>
                    </MenuTrigger>

                    <MenuPopover>
                      <MenuList>
                        {(translatorAgent.targetLanguages || []).map(lang => (
                          <MenuItemRadio key={lang} name="language" value={lang}>
                            {lang}
                          </MenuItemRadio>
                        ))}
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                  {hasAnyTranslation && !isMe && (
                    <div className={styles.translatedLabel} onClick={handleToggleView}>
                      {!isShowingTranslation
                        ? "(See translation)"
                        : onDemandTranslation
                        ? `(Translated to ${onDemandTranslation.language})`
                        : "(See original)"}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </>
      );

      return (
        <ChatMessage
          key={key}
          forceSpawn={true}
          onSpawn={() => spawnChatMessage(displayText)}
          canSpawnMessages={canSpawnMessages}
          sender={sender}
          timestamp={timestamp}
          isMe={isMe}
          footer={footer}
        >
          {formattedBody}
          {onDemandError && !onDemandLoading && (
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              marginTop: "6px",
              padding: "4px 10px",
              borderRadius: "6px",
              backgroundColor: "var(--color-brand-subtle)",
              border: "1px solid var(--color-danger)",
              fontSize: "0.82em",
              color: "var(--color-danger)",
              lineHeight: 1.3
            }}>
              <span style={{ fontSize: "14px" }}>⚠</span>
              <span>{onDemandError}</span>
            </div>
          )}
        </ChatMessage>
      );
    }
    case "video":
      return (
        <ChatMessage
          key={key}
          onSpawn={onSpawn}
          canSpawnMessages={canSpawnMessages}
          message={message}
          media
          sender={additionalProps.sender}
          timestamp={additionalProps.timestamp}
          isMe={additionalProps?.sent}
        >
          <video controls src={message.body.src} />
        </ChatMessage>
      );
    case "image":
    case "photo":
      return (
        <ChatMessage
          key={key}
          onSpawn={onSpawn}
          canSpawnMessages={canSpawnMessages}
          message={message}
          media
          sender={additionalProps.sender}
          timestamp={additionalProps.timestamp}
          isMe={additionalProps?.sent}
        >
          <img src={message.body.src} />
        </ChatMessage>
      );
    case "audio":
      return (
        <ChatMessage
          key={key}
          onSpawn={onSpawn}
          canSpawnMessages={canSpawnMessages}
          message={message}
          media
          sender={additionalProps.sender}
          timestamp={additionalProps.timestamp}
          isMe={additionalProps?.sent}
        >
          <audio style={{ maxWidth: "250px" }} controls src={message.body.src} />
        </ChatMessage>
      );
    case "permission":
      return (
        <ChatMessage
          key={key}
          onSpawn={onSpawn}
          canSpawnMessages={canSpawnMessages}
          message={message}
          media
          sender={additionalProps.sender}
          timestamp={additionalProps.timestamp}
          isMe={additionalProps?.sent}
        >
          <img src={message.body.src} />
        </ChatMessage>
      );
    case "object": {
      if (message.body?.src || message?.body?.name) {
        const url = message?.body?.src ? new URL(message?.body?.src) : "";
        const urlFilename = url?.pathname?.split("/")?.pop();
        const { formattedBody } = formatMessageBody(message?.body?.name || urlFilename);
        return (
          <ChatMessage
            key={key}
            onSpawn={onSpawn}
            canSpawnMessages={canSpawnMessages}
            message={message}
            media
            sender={additionalProps.sender}
            timestamp={additionalProps.timestamp}
            isMe={additionalProps?.sent}
          >
            <p style={{ fontStyle: "italic" }}>{formattedBody}</p>
          </ChatMessage>
        );
      }
    }
    // eslint-disable-next-line no-fallthrough
    default:
      return null;
  }
}

MessageComponent.propTypes = {
  message: PropTypes.object.isRequired
};

const MemoizedMessageComponent = React.memo(MessageComponent);

export const EmojiPickerPopoverButton = React.memo(function EmojiPickerPopoverButton({ onSelectEmoji }) {
  const styles = useStyles();
  // We're using a ref here, since we don't want to re-render anything, but we
  // do want to know if the Shift key is down when an emoji is selected.
  const shiftKeyDown = useRef(false);

  const [open, setOpen] = useState(false);

  const handleOpenChange = (_, data) => {
    setOpen(data.open);
  };

  useEffect(() => {
    const onKeyDown = e => {
      if (e.key === "Shift") shiftKeyDown.current = true;
    };
    const onKeyUp = e => {
      if (e.key === "Shift") shiftKeyDown.current = false;
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, []);

  return (
    <div position="center">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger>
          <Button className={styles.inputIcon} appearance="transparent" icon={<Emoji24Regular />} />
        </PopoverTrigger>
        <PopoverSurface>
          <EmojiPicker
            onSelect={emoji => {
              const keepPickerOpen = shiftKeyDown.current;
              onSelectEmoji({ emoji, pickerRemainedOpen: keepPickerOpen });
              if (!keepPickerOpen) {
                setOpen(false);
              }
            }}
          />
        </PopoverSurface>
      </Popover>
    </div>
  );
});

EmojiPickerPopoverButton.propTypes = {
  onSelectEmoji: PropTypes.func.isRequired
};

export function MessageAttachmentButton(props) {
  const styles = useStyles();

  const fileInputRef = useRef(null);

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <>
      <Button
        as="label"
        className={styles.inputIcon}
        appearance="transparent"
        icon={<AttachRegular />}
        onClick={handleButtonClick}
      />
      <input type="file" style={{ display: "none" }} disabled={props.disabled} ref={fileInputRef} {...props} />
    </>
  );
}
